import React, { Suspense, useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { authProtectedRoutes, publicRoutes } from './allRoutes';
import Layout from 'core/layouts';
import NonAuthLayout from '../../layouts/NonLayout';
import AuthProtected from './AuthProtected';
import ErrorBoundary from '../../components/common/ErrorBoundary';
import authService from '../../services/auth.service';

// Custom hook to cleanup body scroll on route changes
const useRouteChangeCleanup = () => {
  const location = useLocation();

  useEffect(() => {
    // Clean up body scroll classes/styles when route changes
    // This prevents stuck scroll from modals, sidebars, etc.
    document.body.classList.remove('overflow-hidden');
    document.body.style.overflow = '';
  }, [location.pathname]);
};

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-500"></div>
  </div>
);

const RouteIndex = () => {
  // Cleanup body scroll on route changes
  useRouteChangeCleanup();

  // A stored session (even one whose access token has since gone stale)
  // should land on a protected route so AuthProtected can decide whether
  // it's still good or needs a silent refresh — NOT get bounced straight
  // to the login form. Previously "/" and the catch-all below always went
  // to /login unconditionally, regardless of auth state: reopening the
  // app in a fresh tab (which typically lands on "/", not a deep link)
  // showed the login screen even seconds after a successful login, well
  // within the access token's own lifetime. Only a genuinely logged-out
  // visitor (no stored session at all) should ever see /login from here.
  const hasStoredSession = !!authService.getCurrentUser();
  const rootRedirect = hasStoredSession ? '/loyalty/dashboard' : '/login';

  return (
    <React.Fragment>
      <Routes>
        {/* Default route */}
        <Route path="/" element={<Navigate to={rootRedirect} replace />} />

        {/* Analytics base route - redirect to first sub-route */}
        <Route path="/analytics" element={<Navigate to="/analytics/influencer-reward-program" replace />} />

        {authProtectedRoutes.map((route, idx: number) => (
          <Route
            key={idx}
            path={route.path}
            element={
              <AuthProtected>
                <Layout>
                  <ErrorBoundary>
                    <Suspense fallback={<PageLoader />}>
                      <route.component />
                    </Suspense>
                  </ErrorBoundary>
                </Layout>
              </AuthProtected>
            }
          />
        ))}
        {publicRoutes.map((route, idx: number) => (
          <Route
            path={route.path}
            key={idx}
            element={
              <NonAuthLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageLoader />}>
                    <route.component />
                  </Suspense>
                </ErrorBoundary>
              </NonAuthLayout>
            } />
        ))}

        {/* Catch-all route */}
        <Route path="*" element={<Navigate to={rootRedirect} replace />} />
      </Routes>
    </React.Fragment>
  );
};

export default RouteIndex;
